import React, { Fragment, useEffect, useState } from 'react';
import { Auftrag, Sendungsstatus } from '../../../../../api';
import { connect } from 'react-redux';
import {
  calcCurrentDate,
  utcIsoDateToLocaleDate,
  utcIsoDateToLocaleDateAndTimeAndDay,
} from '../../../../../utils/date';

import { StateModel } from '../../../../../redux/store';
import { SendungsstatusModel } from '../../../../../models/SendungsstatusModel';
import { byAnzeigefolge } from '../../../../../utils/comparators';
import { ConfigModel } from '../../../../../models/ConfigModel';

interface AuftragsresearchProps {
  selectedAuftrag: Auftrag | undefined;
  defaultstatuskette: Sendungsstatus[];
  anzeigestati: Sendungsstatus[];
  config: ConfigModel;
}

/**
 * AuftragSidebarTab-Component
 *
 * Dient zur anzeigeList von Auftragsinformationen
 *
 * @returns {JSX.Element} AuftragSidebarTab-Component
 * @constructor
 */
const AuftragHistorieSidebarTab = (props: AuftragsresearchProps) => {
  const [gesamtStatusListe, setGesamtStatusListe] = useState<
    Array<SendungsstatusModel>
  >([{ id: -1 }]);

  const findAnzeigestatusBezeichnung = (
    sdgstatus: Sendungsstatus | undefined
  ) => {
    if (!sdgstatus) return 'Geheimstatus';
    const status = props.anzeigestati.find(
      status => status.id == sdgstatus.sdst_id_anzeige
    );
    return status?.kurzBezeichnung ?? sdgstatus.kurzBezeichnung;
  };

  useEffect(() => {
    initGesamtStatusListe();
  }, [props.selectedAuftrag]);

  const initGesamtStatusListe = () => {
    gesamtStatusListe.splice(0);

    props.defaultstatuskette.map(status =>
      gesamtStatusListe.push({
        id: status.id ?? -1,
        anzeige: status.anzeige,
        awSperre: status.awSperre,
        defaultstatus: status.defaultstatus,
        sdst_id_anzeige: status.sdst_id_anzeige,
        anzeigefolge: status.anzeigefolge,
        icon: status.icon + ' lastActiveIcon',
        kurzBezeichnung: status.kurzBezeichnung,
        datum: undefined,
        date: undefined,
        kommentar: undefined,
        quittiert: undefined,
        ereignisort: undefined,
      })
    );

    setGesamtStatusListe(gesamtStatusListe);
  };

  const findAnzeigestatusIcon = (sdgstatus: Sendungsstatus | undefined) => {
    if (!sdgstatus) return 'Geheimstatus';
    const status = props.anzeigestati.find(
      status => status.id == sdgstatus.sdst_id_anzeige
    );
    return status?.icon ?? 'fas fa-shipping-fast';
  };

  const setStatusList = (): SendungsstatusModel[] => {
    const auftragshistorie = props.selectedAuftrag?.auftragshistorie;

    if (gesamtStatusListe.length <= 1) {
      initGesamtStatusListe();
    }

    for (const i in gesamtStatusListe) {
      // Status Transportdaten erfasst
      gesamtStatusListe[i].datum = '';
      if (gesamtStatusListe[i].id === 1000001) {
        gesamtStatusListe[i].datum = utcIsoDateToLocaleDateAndTimeAndDay(
          '' + props.selectedAuftrag?.datum
        );
        gesamtStatusListe[i].date = new Date(
          props.selectedAuftrag?.datum ?? ''
        );

        if (
          props.selectedAuftrag?.status?.id &&
          props.selectedAuftrag?.status?.id > 1
        ) {
          gesamtStatusListe[i].icon = 'fas fa-circle';
        }
        // Status Transportdaten übertragen
      } else if (gesamtStatusListe[i].id === 1000002) {
        const austatusId = props.selectedAuftrag?.status?.id;
        gesamtStatusListe[i].datum =
          austatusId && austatusId >= 3 ? 'ist erfolgt' : '';

        const neuesDatum = new Date(props.selectedAuftrag?.datum ?? '');
        gesamtStatusListe[i].date = new Date(
          new Date(neuesDatum).getTime() + 1000
        );

        if (auftragshistorie?.length ?? 0 > 0) {
          gesamtStatusListe[i].icon = 'fas fa-circle';
        } else {
          if (austatusId && austatusId < 3) {
            gesamtStatusListe[i].icon = 'far fa-circle';
          }
        }
      }
    }
    // wenn Sendungsstati vorhanden
    if (auftragshistorie) {
      for (const h of auftragshistorie) {
        const statusToFind =
          h.sendungsstatus?.sdst_id_anzeige ?? 0 > 0
            ? h.sendungsstatus?.sdst_id_anzeige
            : h.sendungsstatus?.id;

        const eintrag = gesamtStatusListe.find(
          liste => liste.id == statusToFind
        );

        // wenn der Status gleich dem Status in der Liste
        if (eintrag && eintrag.id === statusToFind) {
          eintrag.originalSendungsstatusId = h.sendungsstatus?.id;
          eintrag.datum = utcIsoDateToLocaleDateAndTimeAndDay(h.datum || '-');
          eintrag.date = new Date(h.datum ?? new Date());
          eintrag.sdst_id_anzeige = h.sendungsstatus?.sdst_id_anzeige;
          eintrag.kommentar = h.kommentar;
          eintrag.quittiert = h.quittiert;
          eintrag.ereignisort = h.ereignisort;
          if (props.selectedAuftrag?.sdstId !== h.sendungsstatus?.id) {
            eintrag.icon = 'fas fa-circle';
          } else {
            eintrag.icon = eintrag.icon + ' lastActiveIcon';
          }
        } else {
          // hinzufügen
          if (statusToFind) {
            const icon =
              props.selectedAuftrag?.sdstId !== h.sendungsstatus?.id
                ? 'fas fa-circle'
                : findAnzeigestatusIcon(h.sendungsstatus);

            gesamtStatusListe.push({
              id: statusToFind,
              anzeigefolge: gesamtStatusListe.length + 1,
              icon: icon,
              datum: utcIsoDateToLocaleDateAndTimeAndDay(h.datum || '-'),
              date: new Date(h.datum ?? new Date()),
              kurzBezeichnung: findAnzeigestatusBezeichnung(h.sendungsstatus),
              kommentar: h.kommentar,
              quittiert: h.quittiert,
              ereignisort: h.ereignisort,
            });
          }
        }
      }
    }

    let anzeigeListSort = [...gesamtStatusListe];

    anzeigeListSort = anzeigeListSort.sort(byAnzeigefolge);
    //anzeigeListSort = anzeigeListSort.sort(byDatumAscending);

    // wenn der letzte Anzeigestatus aus der Defaultstatuskette erreicht ist, dann die noch nicht gesetzten Stati entfernen

    const lastDefaultStatusId = props.defaultstatuskette.slice(-1)[0].id;
    const lastHero = anzeigeListSort.find(
      status =>
        status.originalSendungsstatusId === props.selectedAuftrag?.sdstId &&
        status.id === lastDefaultStatusId
    );

    if (!!lastHero) {
      const filterAnzeigeListSort = anzeigeListSort.filter(function (f) {
        return f.date !== undefined;
      });

      return filterAnzeigeListSort;
    }
    return anzeigeListSort;
  };

  const geplanteZustellung = () => {
    let deliveryDate;
    if (props.selectedAuftrag?.zustellDatumVon != undefined) {
      deliveryDate = props.selectedAuftrag?.zustellDatumVon;
    } else {
      deliveryDate = calcCurrentDate(+3, false);
    }
    // Status 129 geplante Zustellzeit wird bei Bexity ständig upgedatet.
    if (props.config.clientKunde?.toUpperCase() == 'BEXITY') {
      const status129 = props.selectedAuftrag?.auftragshistorie?.find(
        status => status.sendungsstatus?.id === 129
      );
      deliveryDate =
        status129?.kommentar ?? utcIsoDateToLocaleDate(deliveryDate);
      props.selectedAuftrag?.auftragshistorie;
      if (
        props.selectedAuftrag?.verkehrsart?.id == 4 &&
        props.selectedAuftrag?.empfaengerAdresse?.land?.id != 1
      ) {
        return;
      }
      return (
        <div className={'zustellung'}>
          {`geplante Zustellung: ${deliveryDate} `}
          {status129 && <i className='fas fa-flag-checkered color-green' />}
        </div>
      );
    }
    return (
      props.config.clientKunde?.toUpperCase() !== 'MMT' && (
        <div className={'zustellung'}>
          {`geplante Zustellung: ${utcIsoDateToLocaleDate(deliveryDate)}`}
        </div>
      )
    );
  };

  const historyList = (dah: SendungsstatusModel) => {
    return (
      <Fragment key={dah.id}>
        <div className={'icons'}>
          {dah.id != 1000001 && <div className={'vl'} />}
          <i className={dah.datum ? dah.icon : 'far fa-circle'} />
        </div>
        <label
          className={dah.datum ? 'status' : 'disabledStatus'}
          title={
            'Status:           ' +
            dah.kurzBezeichnung +
            '\nKommentar:  ' +
            (dah.kommentar === undefined || dah.kommentar === null
              ? ' '
              : dah.kommentar) +
            '\nQuittiert:       ' +
            (dah.quittiert === undefined || dah.quittiert === null
              ? ' '
              : dah.quittiert)
          }
        >
          {dah.kurzBezeichnung}
        </label>
        <div className={'info'}>
          <p>
            {dah.datum + ' ' + (dah.ereignisort ? ', ' + dah.ereignisort : '')}
          </p>
          <p> {dah.kommentar}</p>
          <p>{dah.quittiert}</p>
        </div>
      </Fragment>
    );
  };
  return (
    <div className={'tab-content'}>
      <div className={'info-block'}>
        <div className={'fs-16-5'}>
          <strong>
            {props.selectedAuftrag?.spedeigennr ??
              props.selectedAuftrag?.spedeigennr}
          </strong>
          {geplanteZustellung()}
        </div>
      </div>

      {/*Historie in historischer Reihenfolge*/}
      <div className={'info-block'}>
        {setStatusList().map(dah => {
          return historyList(dah);
        })}
      </div>
    </div>
  );
};

const mapStateToProps = (state: StateModel) => ({
  defaultstatuskette: state.Stammdaten.defaultstatuskette,
  anzeigestati: state.Admin.anzeigeStati,
  config: state.Config,
});

export default connect(mapStateToProps, {})(AuftragHistorieSidebarTab);
